<script setup lang="ts">
import type { Group, GroupRecord } from '@/components/domain/record-link/fields';
import { ChevronDown, ChevronUp, X } from 'lucide';
import { computed, ref, watch } from 'vue';
import { SphButton } from '@/components/base/button';
import { useI18n } from '@/composables/i18n';
import { useCardText } from './cardText';
import RecordDifferences from './RecordDifferences.vue';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    group: Group
    canManage: boolean
    pending: boolean
    expandAll: boolean
}>();

const emit = defineEmits<{
    'set-main': [record: GroupRecord]
    'clear-main': []
    remove: [record: GroupRecord]
    add: []
    dissolve: []
}>();

const { i18n } = useI18n();
const { distinctNames, recordsLine: recordsLineOf, differencesSummary: differencesSummaryOf } = useCardText();

const open = ref(props.expandAll);
watch(() => props.expandAll, (expandAll) => {
    open.value = expandAll;
});

const name = computed(() => distinctNames(props.group.records.map((record) => record.displayName)));
const recordsLine = computed(() => recordsLineOf(props.group.records.map((record) => record.institutionName)));

const divergentFields = computed(() => props.group.fields.filter(
    (field) => props.group.records.some((record) => record.divergentFields.includes(field)),
));
const differencesSummary = computed(() => differencesSummaryOf(divergentFields.value));

const stateLabel = computed(() => {
    switch (props.group.state) {
        case 'resolved': return i18n.t('Hauptdatensatz festgelegt');
        case 'conflict': return i18n.t('Konflikt bei der Ermittlung');
        default: return i18n.t('Ohne Hauptdatensatz');
    }
});

const stateBadgeClass = computed(() => {
    switch (props.group.state) {
        case 'resolved': return 'kgr-badge--success';
        case 'conflict': return 'kgr-badge--error';
        default: return 'kgr-badge--warning';
    }
});

function mainSourceLabel(): string | null {
    switch (props.group.mainSource) {
        case 'manual':
            return i18n.t('manuell festgelegt');
        case 'claimed':
            return i18n.t('vom Verein beansprucht');
        case 'derived': {
            const rule = props.group.mainDecidedBy;

            return rule === null ? i18n.t('automatisch ermittelt') : `${i18n.t('automatisch ermittelt')} (${rule})`;
        }
        default:
            return null;
    }
}

const mainSourceLine = computed(() => {
    if (props.group.state !== 'resolved') {
        return null;
    }
    const label = mainSourceLabel();
    if (label === null) {
        return null;
    }
    const setAt = props.group.mainSetAt === null ? '' : ` (${props.group.mainSetAt})`;

    return `${label}${setAt}`;
});
</script>

<template>
    <article class="group-card">
        <div class="group-card__head">
            <div class="group-card__heading">
                <h3 class="kgr-headline kgr-headline--h4 group-card__name">
                    {{ name }}
                </h3>
                <div class="kgr-text kgr-text--light group-card__records-line">
                    {{ recordsLine }}
                </div>
            </div>
            <div class="group-card__badges">
                <span
                    class="kgr-badge"
                    :class="stateBadgeClass"
                >{{ stateLabel }}</span>
                <span
                    v-if="group.mainStale"
                    class="kgr-badge kgr-badge--warning"
                >{{ $t('Nicht mehr durch Regel gedeckt') }}</span>
            </div>
        </div>

        <div class="group-card__records">
            <div
                v-for="record in group.records"
                :key="record.entryId"
                class="group-card__record"
                :class="{ 'group-card__record--main': record.isMain }"
            >
                <strong class="kgr-text kgr-text--bold group-card__record-club">{{ record.institutionName }}</strong>
                <span
                    v-if="record.isMain"
                    class="kgr-badge kgr-badge--success"
                >{{ $t('Hauptdatensatz') }}</span>
                <SphButton
                    v-else-if="canManage"
                    :label="$t('Als Hauptdatensatz')"
                    variant="transparent"
                    size="sm"
                    class="sm:min-w-0!"
                    :disabled="pending"
                    @click="emit('set-main', record)"
                />
                <SphButton
                    v-if="canManage"
                    :icon="X"
                    variant="transparent"
                    size="sm"
                    :aria-label="$t('Entfernen')"
                    :title="$t('Entfernen')"
                    :disabled="pending"
                    @click="emit('remove', record)"
                />
                <div
                    v-if="record.isMain && mainSourceLine !== null"
                    class="kgr-text kgr-text--light kgr-text--small w-full"
                >
                    {{ mainSourceLine }}
                </div>
            </div>
        </div>

        <div
            v-if="canManage && group.state === 'resolved'"
            class="px-2"
        >
            <SphButton
                :label="$t('Hauptdatensatz aufheben')"
                variant="transparent"
                size="sm"
                class="sm:min-w-0! kgr-text--underlined"
                :disabled="pending"
                @click="emit('clear-main')"
            />
        </div>

        <div
            v-if="group.note !== null"
            class="kgr-text kgr-text--light group-card__notes"
        >
            {{ $t('Notiz') }}: {{ group.note }}
        </div>

        <div
            v-if="differencesSummary !== null"
            class="group-card__summary"
        >
            <SphButton
                :label="differencesSummary"
                variant="transparent"
                size="sm"
                :icon-right="open ? ChevronUp : ChevronDown"
                :aria-expanded="open"
                @click="open = !open"
            />
        </div>

        <RecordDifferences
            v-if="open && differencesSummary !== null"
            :records="group.records"
            :fields="group.fields"
            :divergent-fields="divergentFields"
            class="group-card__differences"
        />

        <div
            v-if="canManage"
            class="group-card__actions"
        >
            <SphButton
                :label="$t('Datensatz hinzufügen')"
                size="sm"
                class="flex-1"
                :disabled="pending"
                @click="emit('add')"
            />
            <SphButton
                :label="$t('Verknüpfung auflösen')"
                variant="destructive"
                size="sm"
                class="flex-1"
                :disabled="pending"
                @click="emit('dissolve')"
            />
        </div>
    </article>
</template>

<style scoped lang="scss">
.group-card {
    display: flex;
    flex-direction: column;
    min-width: 0;
    border: 1px solid var(--kgr-card-border-color);
    border-radius: 12px;
    background-color: var(--kgr-color-white);
    box-shadow: 0 1px 2px color-mix(in srgb, var(--kgr-color-black) 6%, transparent);
}

.group-card__head {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 1rem 1rem 0.5rem;
}

.group-card__heading {
    flex: 1;
    min-width: 0;
}

.group-card__name {
    overflow-wrap: anywhere;
}

.group-card__records-line {
    margin-top: 0.25rem;
    overflow-wrap: anywhere;
}

.group-card__badges {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-shrink: 0;
    gap: 0.25rem;
}

.group-card__records {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    padding: 0.125rem 1rem 0.25rem;
}

.group-card__record {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.125rem 0.5rem;
    padding: 0.5rem 0.625rem;
    border-radius: var(--radius-md);
    background-color: var(--kgr-color-neutral-lighter);

    &--main {
        background-color: color-mix(in srgb, var(--kgr-color-success) 10%, transparent);
    }
}

.group-card__record-club {
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
}

.group-card__notes {
    padding: 0.25rem 1rem 0;
    overflow-wrap: anywhere;
}

.group-card__summary {
    padding: 0 0.5rem;

    :deep(.sph-button) {
        height: auto;
        max-width: 100%;
        white-space: normal;
        text-align: left;
        line-height: 1.3;
        font-weight: var(--font-weight-semibold);
        color: var(--kgr-color-warning);
    }

    :deep([data-label]) {
        min-width: 0;
        overflow-wrap: anywhere;
    }
}

.group-card__differences {
    padding: 0.25rem 1rem 0.5rem;
}

.group-card__actions {
    display: flex;
    gap: 0.5rem;
    margin-top: auto;
    padding: 0.75rem 1rem 1rem;
}
</style>
